// src/pages/BidTemplate/BidTemplate.jsx
// BidCalculatorPage — updated to Pulse brand tokens

import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  CalculatorIcon,
  DocumentTextIcon,
  BookmarkIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import BidWizard             from '../../components/bids/BidWizard';
import DocumentManagementTab from '../../components/bids/documents/DocumentManagementTab';
import SavedBidsTab          from '../../components/bids/scenarios/SavedScenarioTab';
import { BidCalculatorHeader } from '../../components/bids/shared/Header';
import { ToastContainer }    from '../../components/common/Toast';

import { useToast }              from '../../hooks/useToast';
import { useQueryClient }         from '@tanstack/react-query';
import {
  useListBidScenarios,
  useCreateBidScenario,
} from '../../hooks/bid_wizard/useBidScenarios';

const BidCalculatorPage = () => {
  const { toasts, removeToast } = useToast();
  const queryClient = useQueryClient();

  // Invalidate any real-API document cache so the Documents tab
  // always loads from the mock when USE_MOCK=true.
  useEffect(() => {
    if (process.env.REACT_APP_USE_MOCK_API === 'true') {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['bid-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['engagements', 'won'] });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeTab, setActiveTab] = useState('wizard');

  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [documents,    setDocuments]    = useState([]);
  const [loadedScenario, setLoadedScenario] = useState(null);

  const [showSaveModal,       setShowSaveModal]       = useState(false);
  const [pendingSave,         setPendingSave]         = useState(null);
  const [scenarioName,        setScenarioName]        = useState('');
  const [scenarioDescription, setScenarioDescription] = useState('');
  const [resetCounter,        setResetCounter]        = useState(0);

  useEffect(() => {
    document.title = 'Pulse: Bid Calculator';
  }, []);

  // Reset wizard to a fresh Step 1 state when the bid template demo kicks off,
  // so the demo's scripted clicks always start from a known state.
  useEffect(() => {
    const onResetForDemo = () => {
      setActiveTab('wizard');
      setLoadedScenario(null);
      setShowSaveModal(false);
      setPendingSave(null);
      setResetCounter((n) => n + 1);
    };
    window.addEventListener('pulse:reset-bid-wizard', onResetForDemo);
    return () => window.removeEventListener('pulse:reset-bid-wizard', onResetForDemo);
  }, []);

  const { data: scenariosData } = useListBidScenarios({ enabled: true });
  const createMutation = useCreateBidScenario();

  const totalScenarios = scenariosData?.total ?? 0;

  const handleDocumentsChange = useCallback((newDocuments) => {
    setDocuments(newDocuments);
  }, []);

  const handleSaveRequest = useCallback((wizardData, calculations, rateOverrides, margin) => {
    setPendingSave({ wizardData, calculations, rateOverrides, margin });
    if (loadedScenario?.name) setScenarioName(loadedScenario.name);
    setShowSaveModal(true);
  }, [loadedScenario]);

  const handleSaveScenario = useCallback(async () => {
    if (!scenarioName.trim()) { alert('Please enter a bid name'); return; }
    if (!pendingSave)          { alert('No bid data to save');    return; }

    const { wizardData, calculations, rateOverrides, margin } = pendingSave;

    const payload = {
      name:             scenarioName.trim(),
      description:      scenarioDescription.trim() || undefined,
      engagementType:   wizardData.engagementType,
      clientName:       wizardData.clientName,
      services:         wizardData.services         ?? [],
      rateOverrides:    rateOverrides               ?? {},
      margin:           margin                      ?? null,
      selectedTemplate: wizardData.selectedTemplate ?? null,
      selectedBaseline: wizardData.selectedBaseline ?? null,
      calculations: {
        bidValue:   calculations.bidValue   ?? calculations.monthlyFee        ?? null,
        minBid:     calculations.minBid     ?? null,
        maxBid:     calculations.maxBid     ?? null,
        totalHours: calculations.totalHours ?? calculations.totalMonthlyHours ?? null,
        totalCost:  calculations.totalCost  ?? null,
      },
    };

    try {
      const created = await createMutation.mutateAsync(payload);
      if (created) setLoadedScenario(created);
      setScenarioName('');
      setScenarioDescription('');
      setPendingSave(null);
      setShowSaveModal(false);
    } catch (err) {
      console.error('Failed to save scenario:', err);
      alert('Failed to save bid. Please try again.');
    }
  }, [scenarioName, scenarioDescription, pendingSave, createMutation]);

  const handleCloseModal = useCallback(() => {
    setShowSaveModal(false);
    setPendingSave(null);
    setScenarioName('');
    setScenarioDescription('');
  }, []);

  const handleLoadScenario = useCallback((scenario) => {
    setLoadedScenario(scenario);
    setActiveTab('wizard');
  }, []);

  const handleNewBid = useCallback(() => {
    setLoadedScenario(null);
    setActiveTab('wizard');
  }, []);

  const initialWizardData = loadedScenario
    ? {
        engagementType:   loadedScenario.engagement_type   ?? null,
        clientName:       loadedScenario.client_name       ?? '',
        services:         loadedScenario.services          ?? [],
        selectedTemplate: loadedScenario.selected_template ?? null,
        selectedBaseline: loadedScenario.selected_baseline ?? null,
        useReference:
          loadedScenario.selected_template?.id ? true
          : loadedScenario.selected_baseline?.id ? false
          : null,
      }
    : null;

  const tabs = [
    {
      id:          'wizard',
      name:        'Bid Wizard',
      icon:        CalculatorIcon,
      description: 'Assemble & customize',
    },
    {
      id:          'documents',
      name:        'Documents',
      icon:        DocumentTextIcon,
      badge:       documents.length > 0 ? documents.length : null,
      description: 'Upload & manage',
    },
    {
      id:          'scenarios',
      name:        'Saved Bids',
      icon:        BookmarkIcon,
      badge:       totalScenarios > 0 ? totalScenarios : null,
      description: 'Load & review',
    },
  ];

  return (
    <div className="page-wrapper">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="layout-container responsive-space">
        <div data-tour="bid-page-header">
          <BidCalculatorHeader loading={false} />
        </div>

        <div className="responsive-space">
          <div className="bg-white rounded-lg shadow-sm border border-grey-200 mb-6">

            {/* ── Tab Navigation ── */}
            <div className="border-b border-grey-200">
              <nav className="flex -mb-px overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon     = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      data-tour={`bid-tab-${tab.id}`}
                      onClick={() => setActiveTab(tab.id)}
                      className={`group relative flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                        isActive
                          ? 'border-brand-500 text-brand-600 bg-brand-50/50'
                          : 'border-transparent text-grey-600 hover:text-darkblue-500 hover:border-grey-300 hover:bg-grey-50'
                      }`}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${
                        isActive ? 'text-brand-500' : 'text-grey-400 group-hover:text-grey-600'
                      }`} />
                      <div className="text-left">
                        <div>{tab.name}</div>
                        <div className="text-xs text-grey-500 font-normal hidden sm:block">{tab.description}</div>
                      </div>
                      {tab.badge && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          isActive
                            ? 'bg-brand-100 text-brand-700'
                            : 'bg-grey-100 text-grey-600'
                        }`}>
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* ── Tab Content ── */}
            {/* Use conditional rendering (not CSS hiding) to prevent DataTable
                render loops caused by filteredDocuments changing reference
                every render in DocumentManagementTab */}
            <div className="p-4 sm:p-6">
              {activeTab === 'wizard' && (
                <BidWizard
                  key={`${loadedScenario?.id ?? 'new'}-${resetCounter}`}
                  initialWizardData={initialWizardData}
                  initialStep={loadedScenario ? 4 : 1}
                  initialMargin={loadedScenario?.margin ?? undefined}
                  initialRateOverrides={
                    loadedScenario?.rate_overrides &&
                    Object.keys(loadedScenario.rate_overrides).length > 0
                      ? loadedScenario.rate_overrides
                      : undefined
                  }
                  onSaveRequest={handleSaveRequest}
                  isSaving={createMutation.isPending}
                />
              )}

              {activeTab === 'documents' && (
                <DocumentManagementTab
                  documents={documents}
                  onDocumentsChange={handleDocumentsChange}
                  apiBaseUrl={apiBaseUrl}
                />
              )}

              {activeTab === 'scenarios' && (
                <SavedBidsTab
                  onLoadScenario={handleLoadScenario}
                  onNewBid={handleNewBid}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Save Scenario Modal ── */}
      {showSaveModal && (
        <div data-tour="save-modal" className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">

            <div className="flex items-center justify-between px-6 py-4 border-b border-grey-200">
              <h2 className="text-lg font-bold text-darkblue-500">Save Bid Scenario</h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 hover:bg-grey-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-grey-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
                  Bid Name *
                </label>
                <input
                  type="text"
                  value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveScenario()}
                  placeholder="e.g., Tech Law Firm - Full Service Retainer"
                  className="w-full px-4 py-2.5 border border-grey-300 rounded-lg focus:ring-2 focus:ring-brand-300 focus:border-brand-400 outline-none text-sm"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
                  Description <span className="text-grey-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={scenarioDescription}
                  onChange={(e) => setScenarioDescription(e.target.value)}
                  placeholder="Add notes about this bid..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-grey-300 rounded-lg focus:ring-2 focus:ring-brand-300 focus:border-brand-400 outline-none resize-none text-sm"
                />
              </div>

              {pendingSave?.calculations && (
                <div className="bg-grey-50 border border-grey-200 rounded-lg p-4">
                  <div className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-3">
                    Bid Summary
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-grey-500 mb-0.5">Monthly Fee</div>
                      <div className="text-lg font-bold text-darkblue-500">
                        ${(pendingSave.calculations.bidValue ?? pendingSave.calculations.monthlyFee ?? 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-grey-500 mb-0.5">Total Hours</div>
                      <div className="text-lg font-bold text-darkblue-500">
                        {(pendingSave.calculations.totalHours ?? pendingSave.calculations.totalMonthlyHours ?? 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex gap-3 rounded-b-xl">
              <button
                data-tour="save-modal-cancel"
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2.5 border border-grey-300 text-grey-600 rounded-lg hover:bg-white transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveScenario}
                disabled={!scenarioName.trim() || createMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? 'Saving…' : 'Save Bid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BidCalculatorPage);